import type { Avatar } from '@il-colpo/shared/account';
import type { Result } from '../account/types.ts';

/**
 * The people of a player (milestone 7, slice 9): friends, requests, blocks, reports and what
 * moderation did. Read and written through the account service, with the player's own session; who is
 * online, and the invitations, come from the game server (`gameServerApi.ts`).
 */

export interface Person {
  userId: string;
  nickname: string;
  tag: number;
  avatar: Avatar;
}

export interface BlockedPerson {
  userId: string;
  nickname: string;
  tag: number;
}

export interface SocialOverview {
  friends: Person[];
  /** Asked to be friends with the player, and waiting for an answer. */
  incoming: Person[];
  /** The player asked, and is waiting for an answer. */
  outgoing: Person[];
  blocked: BlockedPerson[];
}

export type RequestOutcome =
  | 'requested'
  | 'accepted'
  | 'already'
  | 'notFound'
  | 'blocked'
  | 'self'
  | 'accountRequired'
  | 'full';

export type BlockOutcome = 'blocked' | 'already' | 'notFound' | 'self' | 'full';

export const REPORT_REASONS = ['chat', 'name', 'cheating', 'abandon', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export type ReportOutcome = 'reported' | 'notFound' | 'self' | 'already' | 'limit' | 'invalid';

export interface AppNotification {
  id: string;
  kind: 'friend_request' | 'friend_accepted' | 'moderation';
  /** Who it is about, when it is about somebody. */
  nickname: string | null;
  tag: number | null;
  createdAt: string;
  read: boolean;
}

/** Something moderation did to the player that is still in force. */
export interface MyRestriction {
  kind: 'warn' | 'mute' | 'ban';
  reason: string;
  /** When it ends (null: until the team lifts it). */
  untilAt: string | null;
  createdAt: string;
}

export interface SocialClient {
  overview: () => Promise<Result<SocialOverview>>;
  /** Asks somebody, by their handle (`Nickname#0042`), to be a friend. */
  request: (handle: string) => Promise<Result<RequestOutcome>>;
  respond: (
    userId: string,
    accept: boolean,
  ) => Promise<Result<'accepted' | 'declined' | 'notFound'>>;
  /** Ends a friendship, or takes a request back. */
  remove: (userId: string) => Promise<Result>;
  block: (handle: string) => Promise<Result<BlockOutcome>>;
  unblock: (userId: string) => Promise<Result>;
  report: (
    handle: string,
    reason: ReportReason,
    detail: string,
    matchId?: string | null,
  ) => Promise<Result<ReportOutcome>>;
  notifications: () => Promise<Result<AppNotification[]>>;
  /** Marks as read the notifications named (all of them when none is). */
  markRead: (ids?: readonly string[]) => Promise<Result>;
  /** What moderation did to the player that is still in force. */
  restrictions: () => Promise<Result<MyRestriction[]>>;
}
